import { type Direction, DIRECTIONS, opposite } from "../models/ModuleGrid.js";
import { shapeDef, symbolForPorts } from "./moduleShapes.js";
import { footprintCells, type CellOffset } from "./moduleFootprints.js";

/** anchor(a)에서 인접 점유 셀(b)로 향하는 방향. */
function cellDirFrom(a: CellOffset, b: CellOffset): Direction {
  const dx = b.dx - a.dx;
  const dy = b.dy - a.dy;
  if (dx > 0) return "right";
  if (dx < 0) return "left";
  if (dy > 0) return "down";
  return "up";
}

function unique(ports: readonly Direction[]): Direction[] {
  return [...new Set(ports)];
}

/** 2포트가 서로 마주보면(예: left↔right) 직선, 아니면 꺾임(코너). 이름 의존 없이 모양 분류. */
function isStraightPair(ports: readonly Direction[]): boolean {
  return ports.length === 2 && opposite(ports[0]!) === ports[1]!;
}

/**
 * 배치 모듈의 셀별 포트 — 반환 배열 인덱스는 `footprintCells(footprintId)` 순서와 1:1.
 *
 * 통일 규칙(다중 셀이어도 모듈 고유 모양 보존, "다중 셀=십자" 강제 제거). shapeId 이름이 아니라
 * 포트 형태로 분류한다(방향 명시 카탈로그라 straight_h/corner_ru 등 이름이 많아짐):
 *  - single        : [shape 포트] 1개.
 *  - 4포트(cross)  : 모든 셀 4방향(원래 십자 의미 유지).
 *  - 직선 2포트    : footprint가 늘어선 방향의 직선([link, linkOpp]) — 모양 방향과 무관히 footprint 따라감.
 *  - 코너 2포트/3포트(t): anchor 셀 = 모양 포트 + 인접셀 연결, 두 번째 셀 = 반대 연결 + 모양 잔여 포트.
 *
 * 점유 셀 간 연결(link/linkOpp)은 항상 포함 → 라우팅(BFS) 끊김 방지.
 * (현재 footprint는 2셀(vert2/horz2)만 — 두 번째 셀 규칙은 2셀 가정. 3셀+ footprint는 후속.)
 */
export function cellPortsForPlaced(shapeId: string, footprintId: string): Direction[][] {
  const cells = footprintCells(footprintId);
  const ports = [...shapeDef(shapeId).ports];
  if (cells.length <= 1) return [ports];

  const link = cellDirFrom(cells[0]!, cells[1]!);
  const linkOpp = opposite(link);

  if (ports.length >= 4) return cells.map(() => [...DIRECTIONS]);
  if (isStraightPair(ports)) return cells.map(() => [link, linkOpp]);

  // corner(2포트 비직선) / t(3포트) — anchor에 모양 포트(+연결), 두 번째 셀은 반대 연결 + 모양 잔여.
  const anchorPorts = unique([...ports, link]);
  const tailPorts = unique([linkOpp, ...ports.filter((p) => p !== link)]);
  return [anchorPorts, tailPorts];
}

/**
 * 팔레트 미리보기 심볼 — footprint **방향대로** 셀별 심볼을 배치한다(멀티라인).
 * 세로(vert2)는 줄바꿈(`\n`)으로 위아래, 가로(horz2)는 **공백을 끼워** 좌우로 나열해 1칸(single)과 시각적으로 구분한다
 * (예: straight_h vert2 → "┃\n┃", cross vert2 → "╋\n╋", straight_h horz2 → "━ ━"). single이면 1심볼.
 * 팔레트 라벨은 이 심볼을 멀티라인 Text(align center)로 렌더한다.
 */
export function previewSymbolFor(shapeId: string, footprintId: string): string {
  const symbols = cellPortsForPlaced(shapeId, footprintId).map((p) => symbolForPorts(p));
  if (symbols.length <= 1) return symbols[0] ?? symbolForPorts([]);
  const cells = footprintCells(footprintId);
  const vertical = cells.length >= 2 && (cells[1]?.dy ?? 0) !== 0;
  // 가로(horz2)는 심볼 사이에 공백을 끼워 1칸(single)과 헷갈리지 않게 한다. 세로(vert2)는 줄바꿈.
  return symbols.join(vertical ? "\n" : " ");
}

/** 점유 셀들의 bbox(min/max dx·dy, 칸 단위). footprintSpan(칸 수)·placedShapeLayout(중심 정렬)의 공용 기준점. */
function footprintBBox(cells: readonly CellOffset[]): {
  minDx: number;
  maxDx: number;
  minDy: number;
  maxDy: number;
} {
  let minDx = Infinity, maxDx = -Infinity, minDy = Infinity, maxDy = -Infinity;
  for (const c of cells) {
    if (c.dx < minDx) minDx = c.dx;
    if (c.dx > maxDx) maxDx = c.dx;
    if (c.dy < minDy) minDy = c.dy;
    if (c.dy > maxDy) maxDy = c.dy;
  }
  return { minDx, maxDx, minDy, maxDy };
}

/** footprint 점유 셀 bbox의 칸 수. single=1×1, horz2=2×1, vert2=1×2. 풀/미리보기 스케일 산정에 쓴다. */
export function footprintSpan(footprintId: string): { spanX: number; spanY: number } {
  const { minDx, maxDx, minDy, maxDy } = footprintBBox(footprintCells(footprintId));
  return { spanX: maxDx - minDx + 1, spanY: maxDy - minDy + 1 };
}

/**
 * 배치 모양 렌더 레이아웃 — 셀별 {중심 offset(px), 포트} + 선 길이(half)를 셀 중심 정렬로 계산한다.
 * 큰 그래픽(MAIN_CELL_PX)과 정보 팝업 풀(축소 cellPx)이 **같은 결과**를 그리게 하는 SSoT.
 * single은 1셀이라 segs[0]={ox:0,oy:0}로 자연 흡수(특수 분기 불필요). caller가 drawCellPorts에 넘긴다.
 */
export function placedShapeLayout(
  shapeId: string,
  footprintId: string,
  cellPx: number,
  margin = 4,
): { segs: { ox: number; oy: number; ports: Direction[] }[]; half: number } {
  const cells = footprintCells(footprintId);
  const cellPorts = cellPortsForPlaced(shapeId, footprintId);
  const { minDx, maxDx, minDy, maxDy } = footprintBBox(cells);
  const ctrX = (minDx + maxDx) / 2;
  const ctrY = (minDy + maxDy) / 2;
  const segs = cells.map((c, i) => ({
    ox: (c.dx - ctrX) * cellPx,
    oy: (c.dy - ctrY) * cellPx,
    ports: cellPorts[i] ?? [],
  }));
  return { segs, half: cellPx / 2 - margin };
}

/**
 * 도감 카드 "조각 풀" 합성 (순수) — 풀의 모든 조각을 **각 셀 baseCellPx 고정 척도**로 좌→우 배치해
 * 하나의 Graphics에 그릴 셀 리스트 + 단일 half를 만든다. SSoT로 placedShapeLayout을 조각마다 호출한다.
 *
 * **2칸 조각이 single처럼 또렷하게 보이는 핵심**: 셀 1칸은 항상 baseCellPx(=single 칸과 동일)로 그리고,
 * 2칸 조각은 footprint 폭(spanX×baseCellPx)만큼 가로 공간을 차지하게 둔다. (구버그는 `baseCellPx/maxSpan`으로
 * 2칸 조각의 셀을 절반으로 줄여 레일이 2px로 뭉개졌다 — 이제 폭 초과 시에만 전체 비율 축소.)
 *
 * @param pool 표시할 조각들(shapeId+footprintId). 카드 piecePool.
 * @param baseCellPx 1칸 기준 px(single 칸 크기와 동일). codex.json poolCellPx.
 * @param gap 조각 사이 간격 px.
 * @param maxWidth 가용 폭(px). 합성 전체 폭이 이를 넘을 때만 비율 축소(평소엔 baseCellPx 유지). 미지정이면 무제한.
 * @returns cells(중앙정렬된 셀 offset+ports) + half(레일 길이). caller가 drawCellPorts에 넘긴다.
 */
/**
 * 조각 풀의 **슬롯 레이아웃**만 계산 (순수, 모양 무관) — 각 조각의 중심 x(좌→우, 중앙정렬) + 균일 cellPx + half.
 * composePoolCells(모양 합성)와 "조각별 슬롯에 잠금 배지/숨김"이 필요한 caller(도감 잠금 표시)가 공유하는 SSoT.
 * 전체 풀 폭 기준으로 cellPx를 1회 정하므로, 일부 슬롯만 모양을 그리고 일부는 잠금 표시해도 간격이 안정적이다.
 */
export function composePoolSlots(
  pool: readonly { footprintId: string }[],
  baseCellPx: number,
  gap: number,
  maxWidth?: number,
): { slots: { centerX: number }[]; cellPx: number; half: number } {
  if (pool.length === 0) return { slots: [], cellPx: baseCellPx, half: 0 };
  const totalWidthAt = (px: number) =>
    pool.reduce((sum, p) => sum + footprintSpan(p.footprintId).spanX * px, 0) + gap * (pool.length - 1);
  // 평소엔 baseCellPx 고정(또렷). 합성 폭이 가용 폭을 넘을 때만 비율 축소한다.
  let cellPx = baseCellPx;
  if (maxWidth !== undefined && maxWidth > 0) {
    const full = totalWidthAt(baseCellPx);
    if (full > maxWidth) cellPx = baseCellPx * (maxWidth / full);
  }
  const slots: { centerX: number }[] = [];
  let x = -totalWidthAt(cellPx) / 2;
  for (const p of pool) {
    const w = footprintSpan(p.footprintId).spanX * cellPx;
    slots.push({ centerX: x + w / 2 });
    x += w + gap;
  }
  return { slots, cellPx, half: cellPx / 2 - 2 };
}

export function composePoolCells(
  pool: readonly { shapeId: string; footprintId: string }[],
  baseCellPx: number,
  gap: number,
  maxWidth?: number,
): { cells: { ox: number; oy: number; ports: Direction[] }[]; half: number } {
  const { slots, cellPx, half } = composePoolSlots(pool, baseCellPx, gap, maxWidth);
  const cells: { ox: number; oy: number; ports: Direction[] }[] = [];
  pool.forEach((p, i) => {
    const center = slots[i]!.centerX;
    const r = placedShapeLayout(p.shapeId, p.footprintId, cellPx, 2);
    for (const s of r.segs) cells.push({ ox: s.ox + center, oy: s.oy, ports: s.ports });
  });
  return { cells, half };
}
